import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import { SchoolPeriodUser, SchoolPeriodUserInput } from '@/core/domain/school/user/SchoolPeriodUser'
import { AvailableEducationalMember } from '@/core/domain/school/user/AvailableEducationalMember'
import  { type PaginationOptions, type PaginatedResponse, PaginationHelper } from '@/core/domain/resources/types/Pagination'
import type ISchoolPeriodUserRepository from '@/core/domain/school/user/ISchoolPeriodUserRepository.ts'
import type { SchoolPeriodUserEntity } from '@/core/infrastructure/schemas/school/user/SchoolPeriodUserSchema.ts'
import type { AvailableEducationalMemberEntity } from '@/core/infrastructure/schemas/school/user/AvailableEducationalMemberSchema.ts'

export default class SchoolPeriodUserRepository implements ISchoolPeriodUserRepository {
  async fetchSchoolPeriodUserById(
    schoolId: number,
    periodId: number,
    schoolPeriodUserId: number
  ): Promise<SchoolPeriodUser> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${periodId}/users/${schoolPeriodUserId}`
    )
    return SchoolPeriodUser.create(response.data)
  }

  async fetchAllSchoolPeriodUsers(schoolId: number, schoolPeriodId: number) {
    const response = await apiService.get<SchoolPeriodUser[]>(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/users`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for school period users')
    }

    return response.data.map((schoolPeriodUser) => SchoolPeriodUser.create(schoolPeriodUser))
  }

  async createSchoolPeriodUser(
    schoolId: number,
    periodId: number,
    schoolPeriodUser: SchoolPeriodUserInput
  ): Promise<SchoolPeriodUser> {
    const response = await apiService.post(
      `admin/schools/${schoolId}/periods/${periodId}/users`,
      schoolPeriodUser
    )
    return SchoolPeriodUser.create(response.data)
  }

  async updateSchoolPeriodUser(
    schoolId: number,
    periodId: number,
    schoolPeriodUser: SchoolPeriodUserInput
  ): Promise<SchoolPeriodUser> {
    const response = await apiService.put(
      `admin/schools/${schoolId}/periods/${periodId}/users/${schoolPeriodUser.identifier}`,
      schoolPeriodUser
    )
    return SchoolPeriodUser.create(response.data)
  }

  async fetchSchoolPeriodUserByIdentifier(
    schoolId: number,
    schoolPeriodId: number,
    userIdentifier: string
  ): Promise<SchoolPeriodUser> {
    const response = await apiService.get<SchoolPeriodUserEntity>(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/users/${userIdentifier}`
    )

    return SchoolPeriodUser.create(response.data)
  }

  async fetchAvailableEducationalMembers(
    schoolId: number,
    periodId: number,
    options?: PaginationOptions
  ): Promise<PaginatedResponse<AvailableEducationalMember>> {
    const baseUrl = `admin/schools/${schoolId}/periods/${periodId}/available-educational-members`
    const url = PaginationHelper.buildUrl(baseUrl, options)

    const response = await apiService.get<PaginatedResponse<AvailableEducationalMemberEntity>>(url)

    if (!response.data?.content || !Array.isArray(response.data.content)) {
      throw new Error('Expected paginated response for available educational members')
    }

    // Créer les objets membres un par un pour mieux gérer les erreurs
    const mappedContent: AvailableEducationalMember[] = []
    for (const memberData of response.data.content) {
      try {
        const member = AvailableEducationalMember.create(memberData)
        mappedContent.push(member)
      } catch (err) {
        console.error('Error creating AvailableEducationalMember:', memberData, err)
        throw err
      }
    }

    return {
      content: mappedContent,
      totalElements: response.data.totalElements,
      totalPages: response.data.totalPages,
      currentPage: response.data.currentPage,
      pageSize: response.data.pageSize,
      hasNext: response.data.hasNext,
      hasPrevious: response.data.hasPrevious
    }
  }

  /**
   * Supprime l'association d'un utilisateur et d'une période scolaire
   * @param schoolId
   * @param schoolPeriodId
   * @param userIdentifier
   */
  async deleteSchoolPeriodUser(schoolId: number, schoolPeriodId: number, userIdentifier: string): Promise<void> {
    await apiService.delete(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/users/${userIdentifier}`
    )
  }
}
